import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ChevronDown, FileSignature, FileText, Mail, PenLine, Plus, Search, UserCheck } from 'lucide-react';
import { SelectionActionBar, SelectionCheckbox, useTableSelection } from '../components/TableSelection';
import { getListStatus, matchesBucket } from '../lib/statusModel';
import { lastUpdatedTimestamp } from '../lib/timestamps';

const badgeStyles = {
  action_required: 'bg-amber-50 text-amber-800 border-amber-300',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  draft: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  sent: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  opened: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  ready_to_sign: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  expired: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
};

const badgeLabels = {
  action_required: 'Action required',
  pending: 'Pending',
  signed: 'Signed',
  completed: 'Signed',
  expired: 'Expired',
  revoked: 'Revoked',
};

const DOCUMENT_FILTERS = [
  { v: '', label: 'All' },
  { v: 'signed', label: 'Signed' },
  { v: 'action_required', label: 'Action required' },
  { v: 'pending', label: 'Pending' },
  { v: 'failed', label: 'Failed' },
];

function displayStatusForSignature(item) {
  return getListStatus(item, 'document');
}

function SignatureBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${badgeStyles[status] || badgeStyles.action_required}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {badgeLabels[status] || String(status || 'action_required').replaceAll('_', ' ')}
    </span>
  );
}

function BadgeSequence({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <span className="inline-flex items-center">
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true" className="h-px w-2 bg-zinc-200"/>}
          {child}
        </React.Fragment>
      ))}
    </span>
  );
}

function methodForDocument(item) {
  return item.auth_method === 'kyc' || item.signature_level === 'AES' ? 'AES' : 'SES';
}

function MethodBadges({ method }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
        <FileSignature className="w-3 h-3" strokeWidth={1.8}/>
        {method}
      </span>
    </div>
  );
}

function WorkflowStepBadge({ icon: Icon, title, variant = 'default' }) {
  const variantClass = variant === 'external'
    ? 'rounded-lg border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
    : variant === 'linked'
      ? 'rounded-full border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
      : 'rounded-full border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500';
  return (
    <span className={`inline-flex h-6 min-w-6 items-center justify-center border px-1.5 ${variantClass}`} title={title} aria-label={title}>
      <Icon className="h-3 w-3" strokeWidth={1.8}/>
    </span>
  );
}

function WorkflowBadges({ method }) {
  if (method === 'AES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={UserCheck} title="KYC" variant="linked"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  if (method === 'SES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={Mail} title="Email signing"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  return null;
}

export default function Signatures() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/signatures');
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { signed: 0, action_required: 0, pending: 0, failed: 0 };
    items.forEach((item) => {
      if (matchesBucket(item, 'signed', 'document')) c.signed++;
      else if (matchesBucket(item, 'action_required', 'document')) c.action_required++;
      else if (matchesBucket(item, 'pending', 'document')) c.pending++;
      else if (matchesBucket(item, 'failed', 'document')) c.failed++;
    });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase();
    return items.filter((item) => {
      const displayStatus = displayStatusForSignature(item);
      if (!matchesBucket(item, status, 'document')) return false;
      if (!needle) return true;
      return [
        item.title,
        item.display_name,
        item.signer_name,
        item.signer_first_name,
        item.signer_last_name,
        item.signer_email,
        item.verification_name,
        item.status,
        displayStatus,
      ].some((value) => String(value || '').toLocaleLowerCase().includes(needle));
    });
  }, [items, q, status]);
  const tableSelection = useTableSelection(filtered);
  const selectedStatusLabel = DOCUMENT_FILTERS.find((filter) => filter.v === status)?.label || 'All';
  const statusButtonLabel = status ? `Status: ${selectedStatusLabel}` : 'Status';

  return (
    <div data-testid="signatures-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Documents</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Documents</h1>
        </div>
        <button onClick={() => navigate('/dashboard/signatures/new')}
          className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded-sm hover:bg-zinc-800">
          <Plus className="w-4 h-4" strokeWidth={2}/> New document
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: 'signed', label: 'Signed', v: counts.signed },
          { k: 'action_required', label: 'Action required', v: counts.action_required },
          { k: 'pending', label: 'Pending', v: counts.pending },
          { k: 'failed', label: 'Failed', v: counts.failed },
        ].map((card) => (
          <div key={card.k} className="border border-zinc-200 rounded-sm bg-white p-4" data-testid={`document-stat-${card.k}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{card.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{card.v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div
          className="relative"
          data-testid="document-status-filter"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setStatusMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setStatusMenuOpen((open) => !open)}
            className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300"
            aria-haspopup="listbox"
            aria-expanded={statusMenuOpen}
          >
            {statusButtonLabel}
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
          </button>
          {statusMenuOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
              {DOCUMENT_FILTERS.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => {
                    setStatus(f.v);
                    setStatusMenuOpen(false);
                  }}
                  data-testid={`document-filter-${f.v || 'all'}`}
                  className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${status === f.v ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                  role="option"
                  aria-selected={status === f.v}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-full sm:w-80 h-[34px] flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3">
          <Search className="w-4 h-4 text-zinc-400" strokeWidth={1.5}/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 h-full text-[10px] font-medium uppercase tracking-widest bg-transparent text-zinc-500 focus:outline-none placeholder:text-zinc-400" placeholder="Search documents or signers"/>
        </div>
      </div>

      <SelectionActionBar count={tableSelection.selectedCount} onClear={tableSelection.clearSelection}/>
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500">
              <th className="w-10 px-4 py-3 align-middle font-medium">
                <SelectionCheckbox
                  checked={tableSelection.allVisibleSelected}
                  indeterminate={tableSelection.someVisibleSelected && !tableSelection.allVisibleSelected}
                  onCheckedChange={tableSelection.toggleAllVisible}
                  ariaLabel="Select all visible document rows"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">Document</th>
              <th className="text-left px-4 py-3 font-medium">Identifier</th>
              <th className="text-left px-4 py-3 font-medium">Method</th>
              <th className="text-left px-4 py-3 font-medium">Workflow</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">No signing requests yet.</td></tr>
            ) : filtered.map((item) => {
              const signerName = [item.signer_first_name, item.signer_last_name].filter(Boolean).join(' ')
                || item.signer_name
                || item.signer_email
                || 'Unnamed signer';
              const method = methodForDocument(item);
              const displayStatus = displayStatusForSignature(item);
              const lastUpdatedAt = lastUpdatedTimestamp(item);
              const isSelected = tableSelection.isSelected(item.id);
              return (
              <tr key={item.id} onClick={() => navigate(`/dashboard/signatures/${item.id}`)}
                className={`border-b border-zinc-100 cursor-pointer ${isSelected ? 'bg-zinc-50/80' : 'hover:bg-zinc-50/60'}`}>
                <td className="w-10 px-4 py-4 align-middle">
                  <SelectionCheckbox
                    checked={isSelected}
                    onCheckedChange={() => tableSelection.toggleRow(item.id)}
                    ariaLabel={`Select ${item.title || 'document'}`}
                  />
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-zinc-400 shrink-0" strokeWidth={1.5}/>
                    <div className="font-medium truncate max-w-[12rem]" title={item.title}>{item.title}</div>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="min-w-0">
                    <div className="text-sm leading-tight text-zinc-700 truncate">{signerName}</div>
                    <div className="mt-0.5 text-xs leading-tight text-zinc-500">Signer</div>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle">
                  <MethodBadges item={item} method={method}/>
                </td>
                <td className="px-4 py-4 align-middle">
                  <WorkflowBadges method={method}/>
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <SignatureBadge status={displayStatus}/>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">{lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : ''}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
